import { OPTICAL_PROCEDURE_COPYWRITING_KEY, OPTICAL_PROCEDURE_TITLE_KEYS } from './constants';
import type { OpticalProcedureCopywriting, OpticalProcedureTitleKey } from './types';

const TITLE_KEY_SET = new Set<string>(OPTICAL_PROCEDURE_TITLE_KEYS);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isTitleKey = (value: unknown): value is OpticalProcedureTitleKey =>
  typeof value === 'string' && TITLE_KEY_SET.has(value);

const normalizeTitle = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const normalizeStrategyVariable = (value: unknown) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') return null;

  const strategyVariable = Number(value.trim());
  return Number.isFinite(strategyVariable) ? strategyVariable : null;
};

function assignTitle(target: OpticalProcedureCopywriting, key: unknown, value: unknown) {
  if (!isTitleKey(key)) return;

  const title = normalizeTitle(value);
  if (!title) return;

  target[key] = title;
}

function normalizeStepTitles(target: OpticalProcedureCopywriting, stepTitles: unknown) {
  if (Array.isArray(stepTitles)) {
    stepTitles.forEach((item) => {
      if (!isRecord(item)) return;

      const key = item.key || item.field || item.title_key;
      const value = item.title || item.value || item.copywriting || item.text;
      assignTitle(target, key, value);
    });
    return;
  }

  if (!isRecord(stepTitles)) return;

  Object.entries(stepTitles).forEach(([key, value]) => {
    assignTitle(target, key, value);
  });
}

function getInfoFromResponse(response: unknown) {
  if (!isRecord(response)) return null;
  if (isRecord(response.info)) return response.info;
  if (isRecord(response.data) && isRecord(response.data.info)) return response.data.info;

  return null;
}

export function normalizeOpticalProcedureCopywriting(value: unknown): OpticalProcedureCopywriting {
  if (!isRecord(value)) return {};

  const copywriting: OpticalProcedureCopywriting = {};
  const strategyVariable = normalizeStrategyVariable(value.strategy_variable);
  if (strategyVariable !== null) {
    copywriting.strategy_variable = strategyVariable;
  }

  OPTICAL_PROCEDURE_TITLE_KEYS.forEach((key) => {
    assignTitle(copywriting, key, value[key]);
  });
  normalizeStepTitles(copywriting, value.step_titles);

  return copywriting;
}

export function extractOpticalProcedureCopywritingFromServerResponse(
  response: unknown,
): OpticalProcedureCopywriting {
  const info = getInfoFromResponse(response);
  if (!info) return {};

  return normalizeOpticalProcedureCopywriting(info[OPTICAL_PROCEDURE_COPYWRITING_KEY]);
}

export function extractOpticalProcedureCopywritingFromClientResponse(
  response: unknown,
): OpticalProcedureCopywriting {
  const info = getInfoFromResponse(response);
  if (!info) return {};

  return normalizeOpticalProcedureCopywriting(info[OPTICAL_PROCEDURE_COPYWRITING_KEY]);
}

export function getOpticalProcedureTitle(
  copywriting: OpticalProcedureCopywriting | null | undefined,
  key: OpticalProcedureTitleKey,
  fallback: string,
) {
  if (copywriting?.strategy_variable !== 2) {
    return fallback;
  }

  const title = normalizeTitle(copywriting?.[key]);
  return title || fallback;
}

export function getOpticalProcedureViewAreaTitle(
  copywriting: OpticalProcedureCopywriting | null | undefined,
  fallback: string,
) {
  return getOpticalProcedureTitle(copywriting, 'view_area', fallback);
}
